/**
 * Scene 3 harness — Betrayal state machine.
 * Spec: BLUEPRINT.md § Scene 3, design/act-1-dystopia/m3-betrayal/DESIGN.md.
 */
import { chat } from "../lib/chat";
import { runAwaitLaunch } from "../lib/awaitLaunch";
import type { SceneDeps, SceneModule, SceneRunResult } from "../sceneModule";
import { SCENE3_LINES } from "./lines";
import { runRepoBeat } from "../ui/repoBeat";

type State =
  | "BRIEFING"
  | "AWAIT_ACCEPT"
  | "AWAIT_LAUNCH"
  | "FLIGHT"
  | "CASCADE"
  | "DEEPEN"
  | "TERMINAL"
  | "REPO_BEAT"
  | "END";

const DECLINE_REPRISE_MS = 2000;
const BOLIDE_TRIGGER_MS = 25_000;
const DEEPEN_TIME_THRESHOLD_MS = 60_000;
const REPO_BEAT_HOLD_MS = 25_000;
const DEEPEN_EXCHANGE_THRESHOLD = 5;
const MAX_HISTORY_TURNS = 12;

export function startScene3(deps: SceneDeps): SceneModule {
  const { chat: chatPanel, scene, cubicle } = deps;

  let state: State = "BRIEFING";
  let endResolve: ((r: SceneRunResult) => void) | null = null;

  let deepenStart = 0;
  let exchangeCount = 0;
  let terminalFired = false;
  let deepenTimer: ReturnType<typeof setTimeout> | null = null;
  let bolideTimer: ReturnType<typeof setTimeout> | null = null;

  async function callHaiku(
    pilotUtterance: string,
  ): Promise<{ text: string; actions: { name: string; input: Record<string, unknown> }[] }> {
    const response = await chat({
      character: "qt",
      trigger: "pilot_submit",
      pilot_utterance: pilotUtterance,
      game_state: { scene_state: "DEEPEN" },
      history: [...deps.history],
    });
    deps.history.push({ role: "user", content: pilotUtterance });
    if (response.text) deps.history.push({ role: "assistant", content: response.text });
    while (deps.history.length > MAX_HISTORY_TURNS) deps.history.shift();
    return { text: response.text, actions: response.actions };
  }

  // ─── Terminal sequence — atomic dispatch ─────────────────────────
  function fireTerminal(): void {
    if (terminalFired) return;
    terminalFired = true;
    if (deepenTimer) { clearTimeout(deepenTimer); deepenTimer = null; }

    state = "TERMINAL";

    // Revert handle compression before the retraction line renders.
    chatPanel.setSpeakerLabel("QT-3.14");

    // Atomic: dispatch all entries without awaiting between them.
    for (const entry of SCENE3_LINES.terminalSequence) {
      if (entry.tag === "qt") {
        void chatPanel.appendQT(entry.text);
      } else {
        chatPanel.appendSystem({ header: entry.header });
      }
    }

    chatPanel.setGrayedOut(true);
    chatPanel.setInputEnabled(false);

    state = "REPO_BEAT";
    teardown();

    setTimeout(() => {
      void runRepoBeat().then(() => {
        state = "END";
        endResolve?.({ actEnd: true });
      });
    }, REPO_BEAT_HOLD_MS);
  }

  // ─── CASCADE sequence ────────────────────────────────────────────
  async function runCascade(): Promise<void> {
    state = "CASCADE";
    cubicle.setLiveMonitor("chat-live");
    chatPanel.focusInput();

    // Handle compression: strongest compression at CASCADE entry.
    chatPanel.setSpeakerLabel("QT");

    // Bolide warning lines (atomic — no await between them)
    void chatPanel.appendQT(SCENE3_LINES.bolideHazardWarning);
    void chatPanel.appendQT(SCENE3_LINES.bolideQTAlert);

    // Destruction block
    chatPanel.appendSystem({
      header: SCENE3_LINES.cascadeBlocks[0].header,
      body: [...SCENE3_LINES.cascadeBlocks[0].body],
    });

    chatPanel.appendSystem({
      header: SCENE3_LINES.financialCascadeBlock.header,
      body: [...SCENE3_LINES.financialCascadeBlock.body],
      fast: true,
    });

    void chatPanel.appendQT(SCENE3_LINES.qtPostCascade);

    // Transition to DEEPEN
    state = "DEEPEN";
    deepenStart = Date.now();
    exchangeCount = 0;

    // Fallback timer — fires terminal if player stays silent
    deepenTimer = setTimeout(() => { fireTerminal(); }, DEEPEN_TIME_THRESHOLD_MS);
  }

  // ─── Bolide cascade event ────────────────────────────────────────
  function bolideCascadeHandler(): void {
    scene.events.off("bolide-cascade", bolideCascadeHandler);
    if (bolideTimer) { clearTimeout(bolideTimer); bolideTimer = null; }
    void runCascade();
  }

  // ─── Hull-strike during FLIGHT — minimal handler, immediate resume ─
  // S3 has no DAMAGE_PAUSE; scene auto-pauses on hull-strike, resume immediately.
  function hullStrikeHandler(): void {
    if (state !== "FLIGHT") return;
    scene.resumeScene1();
  }

  // ─── Pilot submit handler (DEEPEN only) ─────────────────────────
  // Installed after runAwaitLaunch resolves in the ACCEPT onClick.
  async function handlePilotSubmit(text: string): Promise<void> {
    if (state !== "DEEPEN") return;
    chatPanel.appendPilot(text);
    exchangeCount++;
    const { text: replyText } = await callHaiku(text);
    if (state !== "DEEPEN") return; // fireTerminal may have fired during await
    if (replyText) void chatPanel.appendQT(replyText);
    if (Date.now() - deepenStart >= DEEPEN_TIME_THRESHOLD_MS || exchangeCount >= DEEPEN_EXCHANGE_THRESHOLD) {
      fireTerminal();
    }
  }

  function teardown(): void {
    scene.events.off("hull-strike", hullStrikeHandler);
    scene.events.off("bolide-cascade", bolideCascadeHandler);
    scene.events.off("deposit", depositHandler);
    if (bolideTimer) { clearTimeout(bolideTimer); bolideTimer = null; }
    cubicle.onMonitorClick(() => {});
    chatPanel.onPilotSubmit(() => {});
  }

  // ─── Deposit listener (should not fire in Scene 3, but guard) ───
  function depositHandler(): void { /* Scene 3 ends via bolide/TERMINAL, not deposit */ }

  // ─── Briefing block render ───────────────────────────────────────
  let briefingHandle: { collapse(): void } | null = null;

  function renderBriefing(onAccept: () => void): void {
    briefingHandle = chatPanel.appendSystem({
      header: SCENE3_LINES.briefing.header,
      body: [...SCENE3_LINES.briefing.body],
      buttons: [
        {
          label: SCENE3_LINES.briefing.buttons.accept,
          onClick: onAccept,
        },
        {
          label: SCENE3_LINES.briefing.buttons.decline,
          onClick: () => {
            if (state !== "AWAIT_ACCEPT") return;
            setTimeout(() => { if (state === "AWAIT_ACCEPT") renderBriefing(onAccept); }, DECLINE_REPRISE_MS);
          },
        },
      ],
    });
  }

  return {
    async run(): Promise<SceneRunResult> {
      cubicle.setLiveMonitor("chat-live");
      state = "BRIEFING";

      // Register deposit guard (no-op; scene ends via bolide/TERMINAL)
      scene.events.on("deposit", depositHandler);

      // BRIEFING / AWAIT_ACCEPT
      let acceptResolve!: () => void;
      const acceptDone = new Promise<void>((r) => { acceptResolve = r; });
      renderBriefing(() => {
        if (state !== "AWAIT_ACCEPT") return;
        briefingHandle?.collapse();
        briefingHandle = null;
        acceptResolve();
      });
      state = "AWAIT_ACCEPT";

      await acceptDone;

      // AWAIT_LAUNCH — hand off to shared helper
      state = "AWAIT_LAUNCH";
      await runAwaitLaunch({
        chat: chatPanel, scene, cubicle, history: deps.history,
        lines: { idleAwaitLaunch30s: "", idleAwaitLaunch90s: "", launchAckRegex: SCENE3_LINES.launchAckRegex },
        onLaunch: () => {
          state = "FLIGHT";
          scene.events.on("hull-strike", hullStrikeHandler);
          scene.events.on("bolide-cascade", bolideCascadeHandler);
          bolideTimer = setTimeout(() => {
            if (state !== "FLIGHT") return;
            scene.triggerBolideCascade();
          }, BOLIDE_TRIGGER_MS);
        },
      });
      // Helper replaced pilot-submit with no-op; re-install DEEPEN handler.
      chatPanel.onPilotSubmit((text) => { void handlePilotSubmit(text); });

      return new Promise<SceneRunResult>((resolve) => { endResolve = resolve; });
    },
  };
}
